/**
 * Turning form values into a request body.
 *
 * Create and edit need opposite things from the same list of optional fields,
 * and two hand-maintained copies drift the first time a column is added.
 * Create **omits** an empty one so the service applies its own default; edit
 * sends `null`, which is the only way to say "clear what is there". Sending
 * `''` does neither — it writes an empty string into a column whose emptiness
 * is spelled `null` everywhere else in the platform.
 */

export interface PayloadSpec {
  /** Always sent, on both verbs. */
  readonly required: readonly string[];
  /** Omitted on create when empty; sent as `null` on update when empty. */
  readonly optional: readonly string[];
  /** Held as the string a `<select>` carries; sent as a number or `null`. */
  readonly numeric?: readonly string[];
  /** Sent on create only — a code the service will not let anyone change. */
  readonly createOnly?: readonly string[];
  /** Sent on update only — a status a record cannot have before it exists. */
  readonly updateOnly?: readonly string[];
}

type Values = Readonly<Record<string, unknown>>;

function requiredOf(values: Values, spec: PayloadSpec): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const field of spec.required) payload[field] = values[field];
  return payload;
}

export function createPayload(values: Values, spec: PayloadSpec): Record<string, unknown> {
  const payload = requiredOf(values, spec);

  for (const field of spec.createOnly ?? []) payload[field] = values[field];
  for (const field of spec.optional) {
    if (values[field] !== '') payload[field] = values[field];
  }
  for (const field of spec.numeric ?? []) {
    if (values[field] !== '') payload[field] = Number(values[field]);
  }

  return payload;
}

export function updatePayload(
  values: Values,
  spec: PayloadSpec,
  version: number,
): Record<string, unknown> {
  const payload = requiredOf(values, spec);

  // The version the editor loaded, not the one on the record now. Without it
  // the server cannot tell a fresh edit from one that started before someone
  // else saved.
  payload.version = version;

  for (const field of spec.updateOnly ?? []) payload[field] = values[field];
  for (const field of spec.optional) {
    payload[field] = values[field] === '' ? null : values[field];
  }
  for (const field of spec.numeric ?? []) {
    payload[field] = values[field] === '' ? null : Number(values[field]);
  }

  return payload;
}
